import { memo, useState, useCallback, useEffect } from "react";
import Spinner from "../../components/Spinner";
import {
	getOwners,
	resolveGroupCullingAnimals,
	cullSelectedAnimals,
} from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import Form from "../../components/Form.js";
import useSelectStyles from "../../components/styles/Select";
import {
	Typography,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
} from "@mui/material";
import { BackButton } from "../../components/Buttons.js";

const GroupCulling = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [fetchedOwners, setFetchedOwners] = useState([]);
	const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
	const [step, setStep] = useState(1);
	const [selectedOwner, setSelectedOwner] = useState(null);
	const [automaticAnimals, setAutomaticAnimals] = useState([]);
	const [duplicateGroups, setDuplicateGroups] = useState([]);
	const [selectedDuplicateIds, setSelectedDuplicateIds] = useState([]);
	const [pendingCullingValues, setPendingCullingValues] = useState(null);
	const [draftCullingValues, setDraftCullingValues] = useState(null);

	const classes = useSelectStyles();
	const { success, error } = useSnackbar();

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		try {
			const { owners } = await getOwners();
			setFetchedOwners(owners);
			setOwnersDropdownValues(owners.map((owner) => ({
				value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
				text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
			})));
		} catch (err) {
			error(err);
		}
		setIsLoading(false);
	});

	useEffect(() => {
		fetchData();
	}, []);

	// Helper function to parse animal codes input
	const parseAnimalCodes = (input) => {
		const flockCodes = new Set();
		const registrationCodes = new Set();
		const invalidCodes = [];
		const parts = input.split(',').map(part => part.trim()).filter(part => part);

		for (const part of parts) {
			if (part.includes('-')) {
				const rangeMatch = part.match(/^(\d{4})\s*-\s*(\d{4})$/);

				if (rangeMatch) {
					const [, start, end] = rangeMatch;
					const startNum = parseInt(start, 10);
					const endNum = parseInt(end, 10);

					if (startNum > endNum) {
						invalidCodes.push(part);
						continue;
					}

					for (let currentCode = startNum; currentCode <= endNum; currentCode++) {
						flockCodes.add(currentCode.toString().padStart(4, '0'));
					}
				} else {
					invalidCodes.push(part);
				}
			} else if (/^\d{4}$/.test(part)) {
				flockCodes.add(part);
			} else if (/^\d{12}$/.test(part)) {
				registrationCodes.add(part);
			} else {
				invalidCodes.push(part);
			}
		}

		return {
			flockCodes: [...flockCodes],
			registrationCodes: [...registrationCodes],
			invalidCodes,
		};
	};

	const ownerSelectFormContent = [
		{
			customType: "dropdown",
			id: "owner",
			items: ownersDropdownValues,
			placeholder: "Επιλογή εκτροφής",
			required: true,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Συνέχεια",
		},
	];

	const animalCullingFormContent = [
		{
			customType: "input",
			id: "animalCodes",
			placeholder: "Εισάγετε Κωδικούς Ζώων",
			multiline: true,
			rows: 4,
			required: true,
			value: draftCullingValues?.animalCodes || "",
		},
		{
			customType: "dropdown",
			id: "cullingCode",
			items: [
				{ value: "Σφαγή", text: "Σφαγή" },
				{ value: "Πώληση Αναπαραγ", text: "Πώληση Αναπαραγ" },
				{ value: "Μεγάλη Ηλικία", text: "Μεγάλη Ηλικία" },
				{ value: "Θάνατος", text: "Θάνατος" },
				{ value: "Διάφοροι Λόγοι", text: "Διάφοροι Λόγοι" },
				{ value: "Πάθηση Αναπαραγ", text: "Πάθηση Αναπαραγ" },
				{ value: "Πάθηση Μαστού", text: "Πάθηση Μαστού" },
				{ value: "Πάθηση", text: "Πάθηση" },
				{ value: "Αποβολή", text: "Αποβολή" },
				{ value: "Άγνωστη Αιτία", text: "Άγνωστη Αιτία" },
			],
			placeholder: "Αιτία Απομάκρυνσης",
			required: true,
			defaultValue: draftCullingValues?.cullingCode || "",
		},
		{
			customType: "date-picker",
			id: "cullingDate",
			type: "desktop",
			label: "Ημ/νία Απομάκρυνσης",
			width: "595px",
			background: "third",
			value: draftCullingValues?.cullingDate || null,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Υποβολή Απομάκρυνσης",
		},
	];

	const handleOwnerSubmit = async (values) => {
		setIsLoading(true);
		const ownerData = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner);
		setSelectedOwner({ id: ownerData?._id, code: ownerData?.code });
		setStep(2);
		setIsLoading(false);
	}

	const resetCullingFlow = (clearDraft = true) => {
		setAutomaticAnimals([]);
		setDuplicateGroups([]);
		setSelectedDuplicateIds([]);
		setPendingCullingValues(null);
		if (clearDraft) {
			setDraftCullingValues(null);
		}
	};

	const completeCulling = async (animalIds, values) => {
		const { success: scs, message } = await cullSelectedAnimals(selectedOwner.id, {
			animalIds,
			cullingCode: values.cullingCode,
			cullingDate: values.cullingDate || null,
		});

		if (scs) {
			success(message);
			resetCullingFlow();
			setSelectedOwner(null);
			setStep(1);
			fetchData();
		} else {
			error(message);
		}
	};

	const handleCullingSubmit = async (values) => {
		setIsLoading(true);
		setDraftCullingValues(values);

		if (!values.animalCodes || !values.animalCodes.trim()) {
			error("Παρακαλώ εισάγετε τουλάχιστον έναν κωδικό ζώου");
			setIsLoading(false);
			return;
		}

		const {
			flockCodes,
			registrationCodes,
			invalidCodes,
		} = parseAnimalCodes(values.animalCodes);

		if (invalidCodes.length > 0) {
			error(`Οι ακόλουθοι κωδικοί δεν είναι έγκυροι: ${invalidCodes.join(", ")}`);
			setIsLoading(false);
			return;
		}

		if (flockCodes.length === 0 && registrationCodes.length === 0) {
			error("Δεν βρέθηκαν έγκυροι κωδικοί ζώων");
			setIsLoading(false);
			return;
		}

		const {
			success: scs,
			message,
			automaticAnimals: resolvedAutomaticAnimals = [],
			duplicateGroups: resolvedDuplicateGroups = [],
			preselectedDuplicateAnimalIds = [],
			missingCodes = [],
		} = await resolveGroupCullingAnimals(selectedOwner.id, {
			flockCodes,
			registrationCodes,
		});

		if (!scs) {
			error(message);
			setIsLoading(false);
			return;
		}

		if (missingCodes.length > 0) {
			error(`Τα ακόλουθα ζώα δεν βρέθηκαν: ${missingCodes.join(", ")}. Δεν έγινε καμία ενημέρωση.`);
			setIsLoading(false);
			return;
		}

		if (resolvedDuplicateGroups.length > 0) {
			setAutomaticAnimals(resolvedAutomaticAnimals);
			setDuplicateGroups(resolvedDuplicateGroups);
			setSelectedDuplicateIds(preselectedDuplicateAnimalIds);
			setPendingCullingValues(values);
			setStep(3);
		} else {
			await completeCulling(
				resolvedAutomaticAnimals.map((animal) => animal.id),
				values,
			);
		}
		setIsLoading(false);
	}

	const handleDuplicateSelection = (animalId) => {
		setSelectedDuplicateIds((currentIds) => (
			currentIds.includes(animalId)
				? currentIds.filter((id) => id !== animalId)
				: [...currentIds, animalId]
		));
	};

	const allDuplicateAnimalIds = duplicateGroups.flatMap((group) => (
		group.animals.map((animal) => animal.id)
	));
	const areAllDuplicateAnimalsSelected = allDuplicateAnimalIds.length > 0
		&& allDuplicateAnimalIds.every((animalId) => selectedDuplicateIds.includes(animalId));

	const handleSelectAllDuplicates = () => {
		setSelectedDuplicateIds(
			areAllDuplicateAnimalsSelected ? [] : allDuplicateAnimalIds,
		);
	};

	const handleDuplicateConfirmation = async () => {
		const animalIds = [
			...automaticAnimals.map((animal) => animal.id),
			...selectedDuplicateIds,
		];

		if (animalIds.length === 0) {
			error("Δεν επιλέχθηκαν ζώα για απομάκρυνση");
			return;
		}

		setIsLoading(true);
		await completeCulling(animalIds, pendingCullingValues);
		setIsLoading(false);
	};

	return (
		<>
			<Spinner open={isLoading} />
			{step === 1 && (
				<Box className={classes.outerBox}>
					<Box container flexDirection="column" className={classes.innerBox}>
						<Typography variant="h5" sx={{ margin: "1rem" }}>
							{"Επιλογή εκτροφής"}
						</Typography>
						<Form
							content={ownerSelectFormContent}
							onSubmit={handleOwnerSubmit}
							toResetForm={false}
						/>
					</Box>
				</Box>
			)}
			{step === 2 && (
				<Box className={classes.outerBox}>
					<Box container flexDirection="column" className={classes.innerBox}>
						<Typography variant="h5" sx={{ margin: "1rem" }}>
							Μαζική Απομάκρυνση Ζώων
						</Typography>
						<Box sx={{ margin: "1rem", padding: "1rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
							<Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
								• Μπορείτε να εισάγετε είτε Κωδικούς Εκτροφής (4-ψήφιους), είτε Κωδικούς Γενεαλογικού Βιβλίου (12-ψήφιους).
							</Typography>
							<Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
								• Μπορείτε να εισάγετε κωδικούς ή/και εύρος κωδικών χωρίζοντάς τα με κόμμα. (Π.χ. 0010, 999999990023, 0356-0423).
							</Typography>
							<Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
								• Όλα τα ζώα θα λάβουν την ίδια αιτία απομάκρυνσης και ημερομηνία.
							</Typography>
							<Typography variant="body2">
								• Οι 4-ψήφιοι κωδικοί αναζητούνται στα ζώα της επιλεγμένης εκτροφής. Αν βρεθούν περισσότερα από ένα ζώα, θα επιλέξετε ποια θέλετε να απομακρυνθούν.
							</Typography>
						</Box>
						<Form
							content={animalCullingFormContent}
							onSubmit={handleCullingSubmit}
							toResetForm={false}
						/>
						<BackButton onClick={() => setStep(1)} />
					</Box>
				</Box>
			)}
			{step === 3 && (
				<Box className={classes.outerBox}>
					<Box container flexDirection="column" className={classes.innerBox}>
						<Typography variant="h5" sx={{ margin: "1rem" }}>
							Βρέθηκαν διπλότυποι κωδικοί εκτροφής
						</Typography>
						<Typography variant="body1" sx={{ margin: "0 1rem 1rem" }}>
							Επιλέξτε ποια ζώα θέλετε να απομακρυνθούν.
						</Typography>
						{automaticAnimals.length > 0 && (
							<Typography variant="body2" sx={{ margin: "0 1rem 1rem" }}>
								Τα ζώα με μοναδικό κωδικό έχουν συμπεριληφθεί αυτόματα.
							</Typography>
						)}
						<Button
							variant="outlined"
							color="primary"
							onClick={handleSelectAllDuplicates}
							sx={{ width: "300px", marginBottom: "1rem" }}
						>
							{areAllDuplicateAnimalsSelected ? "Αποεπιλογή όλων" : "Επιλογή όλων"}
						</Button>
						{duplicateGroups.map((group) => (
							<Box
								key={group.flockCode}
								sx={{
									width: "100%",
									maxWidth: "595px",
									marginBottom: "1rem",
									padding: "1rem",
									border: "1px solid #ddd",
									borderRadius: "4px",
								}}
							>
								<Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
									{`Κωδικός Εκτροφής: ${group.flockCode}`}
								</Typography>
								{group.animals.map((animal) => (
									<FormControlLabel
										key={animal.id}
										sx={{ display: "block", marginLeft: 0 }}
										control={(
											<Checkbox
												checked={selectedDuplicateIds.includes(animal.id)}
												onChange={() => handleDuplicateSelection(animal.id)}
											/>
										)}
										label={animal.registrationCode}
									/>
								))}
							</Box>
						))}
						<Button
							variant="contained"
							color="primary"
							onClick={handleDuplicateConfirmation}
							sx={{ width: "300px", marginBottom: "1rem" }}
						>
							Επιβεβαίωση Απομάκρυνσης
						</Button>
						<BackButton onClick={() => {
							resetCullingFlow(false);
							setStep(2);
						}} />
					</Box>
				</Box>
			)}
		</>
	)
}

export default memo(GroupCulling);
